import { useState } from 'react';
import { isNil } from 'lodash';
import dayjs from 'dayjs';
import { useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

import { IneditableUserInfoType, TransferInfoType, TransferUserInfoType } from './types';
import { AccountUserDetailTypeV2 } from '../../common/types/accountUserDetail';
import { setDash } from '../../../../../../lib/common';
import { ACCOUNT_USER_LOGIN_TYPE_TEXT } from '../../../../../../constants/service/app/accountUserConstants';
import { setDateFormat } from '../../../../../../lib/moment';
import { DATE_FORMAT } from '../../../../../../constants/service/common/utilConstants';
import { IUserInfoTransferSectionProps } from './UserInfoTransferSection';
import { useFromTransferUserInfo } from './hooks/useFromTransferUserInfo';
import toastMsg from '../../../../../../lib/toastMsg';
import { useMigrateMemberMutation } from '../queries/useMigrateMemberMutation';
import { USER_DETAIL_QUERY_KEYS } from '../../common/queryKeys';
import { getUserStatusForEdit } from '../../common/utils';

export interface IUserInfoTransferSectionViewModel {
  isActiveTransfer: boolean;
  isActiveTransferResult: boolean;
  userStatus: AccountUserDetailTypeV2['status'];
  ineditableUserInfo: IneditableUserInfoType;
  transferInfo: TransferInfoType | null;
  transferUserInfoResult: TransferUserInfoType;
  handleActiveTransfer: () => void;
  handleOpenTransferResultModal: () => void;
  handleCloseTransferResultModal: () => void;
  handleCloseTransferInfoModal: () => void;
  handleTransferUser: () => void;
}

/**
 * 유저 상세 > 유저 로그인 정보 viewModel
 * 핵심 기능 : 유저 로그인 정보 확인 및 탈퇴 계정 정보 이관
 * 기능 개요 : sns 간편 로그인 이슈가 잦아지면서 계정 탈퇴후 다시 가입을 요청하는 경우 발생 > 탈퇴한 계정의 정보는 그대로 유지하기 위해 계정 정보 이관 기능 필요
 * 계정정보 이관 기능 유저 플로우
 * 1. (비활성 및 탈퇴 계정시) 계정 변경하기 클릭
 * 2. 같은 휴대폰 번호를 가진 활성화된 유저 정보 조회 모달
 * 3. 변경하기 버튼 클릭
 * 4. 변경이후 유저 계정 정보 확인 모달
 * 5. 완료 버튼 클릭
 * 6. 1번 유저 정보를 그대로 유지하고 계정 정보만 2번 계정으로 변경
 *
 * @see https://www.notion.so/amondz/34b04abe556f412baa8f804f876bc27a?pvs=4
 * */
export const useUserInfoTrasferSectionViewModel = (
  props: IUserInfoTransferSectionProps,
): IUserInfoTransferSectionViewModel => {
  const { userDetailInfo } = props;

  const queryClient = useQueryClient();
  //redux store타입을 전부 선언해줄 수 없어서 임시로 any타입 사용
  const [isActiveTransfer, setIsActiveTransfer] = useState(false);
  const [isActiveTransferResult, setIsActiveTransferResult] = useState(false);

  const transferUserInfo = (userInfo: AccountUserDetailTypeV2): TransferUserInfoType => ({
    id: userInfo.id,
    status: !isNil(userInfo.status) ? getUserStatusForEdit(userInfo.status) : null,
    name: userInfo.name || '-',
    account: userInfo.account || '-',
    loginType: !isNil(userInfo.loginType) ? ACCOUNT_USER_LOGIN_TYPE_TEXT[userInfo.loginType] : '-',
    cellPhone: userInfo.cellPhone || '-',
    createDate: dayjs(userInfo.createDate).format(DATE_FORMAT.YMD_DASH_FORMAT),
  });

  const { mutateAsync: migrateMember } = useMigrateMemberMutation({
    onSuccess: () => toastMsg.success('유저 계정 변경하기 성공'),
    onError: (error) => toastMsg.error(error.message),
  });

  //현재 활성화된 계정 정보 조회
  const { fromTransferUserInfo, reset: resetToTrasferUserInfo } = useFromTransferUserInfo({
    currentUserCellPhone: userDetailInfo?.cellPhone,
    isActive: isActiveTransfer,
    onSuccess: (result) => {
      if (!result) {
        toastMsg.error('동일한 번호로 가입된 활성 상태 계정 불러오기 실패');
        setIsActiveTransfer(false);
      }
    },
    onError: (error) => {
      toastMsg.error(error.message);
      setIsActiveTransfer(false);
    },
  });

  const { loginType, account, createDate, updateDate, status } = userDetailInfo;

  /**
   * 유저 로그인 정보 데이터
   * */
  const ineditableUserInfo = {
    loginType: setDash(!isNil(loginType) ? ACCOUNT_USER_LOGIN_TYPE_TEXT[loginType] : null),
    account: setDash(account),
    createDate: setDateFormat(createDate, DATE_FORMAT.FULL_DASH_FORMAT),
    updateDate: setDateFormat(updateDate, DATE_FORMAT.FULL_DASH_FORMAT),
  };

  /**
   * 정보 이관 후 유저 데이터
   * */
  const transferUserInfoResult = transferUserInfo({
    ...userDetailInfo,
    account: fromTransferUserInfo?.account,
    status: fromTransferUserInfo?.status,
  });

  /**
   * 유저상세 > 계정 변경하기 버튼 클릭시
   * 1. 계정 변경하기 모달 노출
   * */
  const handleActiveTransfer = () => {
    setIsActiveTransfer(true);
  };

  /**
   * 유저상세 > 계정 변경하기 버튼 클릭 > 계정 변경하기 모달 > 변경하기 버튼 클릭시
   * 1. 로그인 타입이 다르면 에러 토스트(같은 로그인 타입만 정보 이관 가능)
   * 2. 계정 변경하기 모달 닫기
   * 3. 계정 변경 예상 결과 모달 노출
   * */
  const handleOpenTransferResultModal = () => {
    if (loginType !== fromTransferUserInfo?.loginType) {
      message.error('로그인 타입이 일치하지 않습니다');
      setIsActiveTransfer(false);
      return;
    }

    setIsActiveTransfer(false);
    setIsActiveTransferResult(true);
  };

  /**
   * 유저상세 > 계정 변경하기 버튼 클릭 > 계정 변경하기 모달 > 닫기 버튼 클릭시
   * 1. 계정 변경하기 모달 닫기
   * 2. 조회한 활성화된 유저 정보 초기화
   * */
  const handleCloseTransferInfoModal = () => {
    setIsActiveTransfer(false);
    resetToTrasferUserInfo();
  };

  /**
   * 유저상세 > 계정 변경하기 버튼 클릭 > 계정 변경하기 모달 > 변경하기 버튼 클릭 > 계정 변경 결과 모달 > 완료 버튼 클릭
   * 1. 정보 이관 api 요청
   * 2. 조회한 활성화 유저 정보 초기화
   * 3. 계정 변경 결과 모달 닫기
   * 4. 유저 정보 재요청
   * */
  const handleTransferUser = async () => {
    if (!userDetailInfo.id || !fromTransferUserInfo?.id) return;
    await migrateMember({
      fromUserId: fromTransferUserInfo.id,
      toUserId: userDetailInfo.id,
    });
    resetToTrasferUserInfo();
    setIsActiveTransferResult(false);
    await queryClient.invalidateQueries([...USER_DETAIL_QUERY_KEYS.USER_INFO_V2, userDetailInfo.id]);
  };

  /**
   * 유저상세 > 계정 변경하기 버튼 클릭 > 계정 변경하기 모달 > 변경하기 버튼 클릭 > 계정 변경 결과 모달 > 취소 버튼 클릭
   * 1. 조회한 활성화 유저 정보 초기화
   * 2. 계정 변경 결과 모달 닫기
   * */
  const handleCloseTransferResultModal = () => {
    resetToTrasferUserInfo();
    setIsActiveTransferResult(false);
  };

  return {
    isActiveTransfer,
    isActiveTransferResult,
    ineditableUserInfo,
    userStatus: status,
    transferInfo: fromTransferUserInfo?.id
      ? {
          fromTransferUser: transferUserInfo(fromTransferUserInfo),
          toTransferUser: transferUserInfo(userDetailInfo),
        }
      : null,
    transferUserInfoResult,
    handleTransferUser,
    handleActiveTransfer,
    handleCloseTransferInfoModal,
    handleOpenTransferResultModal,
    handleCloseTransferResultModal,
  };
};
